'use client';

import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { cn } from '@/core/utils/cn';
import { X, CheckCircle, AlertCircle, Info } from 'lucide-react';
import { toastBus } from '@/core/lib/toast-bus';

type ToastType = 'success' | 'error' | 'info' | 'warning';

interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
}

interface ToastContextType {
  toast: (type: ToastType, title: string, message?: string) => void;
  success: (title: string, message?: string) => void;
  error: (title: string, message?: string) => void;
  info: (title: string, message?: string) => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

const icons: Record<ToastType, React.ReactNode> = {
  success: <CheckCircle className="w-4 h-4 text-hms-green" />,
  error: <AlertCircle className="w-4 h-4 text-hms-red" />,
  info: <Info className="w-4 h-4 text-hms-blue" />,
  warning: <AlertCircle className="w-4 h-4 text-hms-gold" />,
};

const borderColors: Record<ToastType, string> = {
  success: 'border-l-hms-green',
  error: 'border-l-hms-red',
  info: 'border-l-hms-blue',
  warning: 'border-l-hms-gold',
};

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((type: ToastType, title: string, message?: string) => {
    const id = Math.random().toString(36).slice(2);
    setToasts((prev) => [...prev, { id, type, title, message }]);
  }, []);

  const removeToast = (id: string) => setToasts((prev) => prev.filter((t) => t.id !== id));

  /* Subscribe to global toast bus for non-React callers (e.g. QueryClient) */
  useEffect(() => {
    return toastBus.subscribe((event) => addToast(event.type, event.title, event.message));
  }, [addToast]);

  const value: ToastContextType = {
    toast: addToast,
    success: (title, message) => addToast('success', title, message),
    error: (title, message) => addToast('error', title, message),
    info: (title, message) => addToast('info', title, message),
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* Toast Container */}
      <div id="toast-container" className="fixed top-4 right-4 z-[100] flex flex-col gap-2 max-w-sm w-full pointer-events-none">
        {toasts.map((t) => (
          <div
            key={t.id}
            id={`toast-${t.id}`}
            className={cn(
              'pointer-events-auto bg-hms-card border border-hms-border border-l-4 rounded-lg p-3 shadow-2xl flex items-start gap-2.5',
              'animate-[toast-dismiss_4s_ease-in-out_forwards]',
              borderColors[t.type]
            )}
            onAnimationEnd={() => removeToast(t.id)}
          >
            <div id={`toast-${t.id}-icon`} className="mt-0.5">{icons[t.type]}</div>
            <div id={`toast-${t.id}-body`} className="flex-1 min-w-0">
              <p id={`toast-${t.id}-title`} className="text-[13px] font-medium text-hms-text">{t.title}</p>
              {t.message && <p id={`toast-${t.id}-message`} className="text-[12px] text-hms-textDim mt-0.5">{t.message}</p>}
            </div>
            <button id={`toast-${t.id}-close`} onClick={() => removeToast(t.id)} className="text-hms-textDim hover:text-hms-text flex-shrink-0">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToastContext() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToastContext must be used within ToastProvider');
  return ctx;
}
